import type { DomainError } from '@core/domain/errors';
import { ErrorCategory, getUserFriendlyMessage } from '@core/domain/errors';

/**
 * Network error interface
 */
interface NetworkErrorObject {
  networkError: {
    name?: string;
    message?: string;
    [key: string]: unknown;
  };
}

/**
 * Type guard for network errors
 */
export function isNetworkError(error: unknown): boolean {
  return typeof error === 'object' && error !== null && 'networkError' in error && (error as NetworkErrorObject).networkError !== null;
}

/**
 * Checks whether a network error represents a timeout or abort scenario.
 */
function isTimeoutNetworkError(networkError: NetworkErrorObject['networkError']): boolean {
  const name = networkError.name ?? '';
  const msg = networkError.message ?? '';

  return [name === 'AbortError', name === 'TimeoutError', msg.includes('signal is aborted'), msg.includes('timed out'), msg.includes('timeout')].some(Boolean);
}

/**
 * Extracts the correlation ID from a network error.
 * The timeout link attaches it to the DOMException abort reason,
 * which may be nested in the networkError or its cause.
 */
function extractCorrelationId(networkError: NetworkErrorObject['networkError']): string | undefined {
  // Check if correlation ID is directly on the error (from our timeoutLink)
  const errorWithCorrelationId = networkError as { correlationId?: string; cause?: { correlationId?: string } };

  if (errorWithCorrelationId.correlationId) {
    return errorWithCorrelationId.correlationId;
  }

  // Check the cause chain (AbortError wraps the DOMException reason)
  if (errorWithCorrelationId.cause?.correlationId) {
    return errorWithCorrelationId.cause.correlationId;
  }

  return undefined;
}

/**
 * Adapts network errors to domain errors
 */
export function adaptNetworkError(error: unknown): DomainError {
  if (!isNetworkError(error)) {
    throw new Error('Not a network error');
  }

  const networkError = (error as NetworkErrorObject).networkError;
  const isTimeout = isTimeoutNetworkError(networkError);
  const code = isTimeout ? 'NETWORK_TIMEOUT' : 'NETWORK_ERROR';
  const defaultMessage = isTimeout ? 'The request timed out' : 'Unable to connect to the server';
  const correlationId = extractCorrelationId(networkError);

  return {
    code,
    message: networkError.message || defaultMessage,
    userMessage: getUserFriendlyMessage(ErrorCategory.SERVICE, defaultMessage, code),
    category: ErrorCategory.SERVICE,
    correlationId,
    details: networkError,
  };
}
